if (!customElements.get('dt-story-tabs')) {
  customElements.define(
    'dt-story-tabs',
    class DtStoryTabs extends HTMLElement {
      connectedCallback() {
        this.modeTabs = Array.from(this.querySelectorAll('[data-config-mode]'));
        this.optionPanels = Array.from(this.querySelectorAll('[data-config-options]'));
        this.tabs = Array.from(this.querySelectorAll('[data-style-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-style-panel]'));
        this.stages = Array.from(this.querySelectorAll('[data-style-stage]'));
        this.stageScroller = this.querySelector('[data-style-scroll]');
        this.caseTabs = Array.from(this.querySelectorAll('[data-case-choice]'));
        this.caseStages = Array.from(this.querySelectorAll('[data-case-stage]'));
        this.caseStageScroller = this.querySelector('[data-case-scroll]');
        this.caseIncluded = Array.from(this.querySelectorAll('[data-case-included]'));
        this.styleCurrentName = this.querySelector('[data-style-current-name]');
        this.styleCurrentDetail = this.querySelector('[data-style-current-detail]');
        this.stylePosition = this.querySelector('.dt-story-config__position[data-style-position]');
        this.caseCurrent = this.querySelector('[data-case-current]');
        this.casePosition = this.querySelector('.dt-story-config__position[data-case-position]');

        const activeStyleTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
        this.panels.forEach((panel) => {
          const selected = panel.id === activeStyleTab?.getAttribute('aria-controls');
          panel.hidden = false;
          panel.classList.toggle('is-active', selected);
          panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
          panel.inert = !selected;
        });

        const activeCaseTab = this.caseTabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.caseTabs[0];
        this.caseIncluded.forEach((panel) => {
          const selected = panel.dataset.caseIncluded === activeCaseTab?.dataset.caseChoice;
          panel.hidden = false;
          panel.classList.toggle('is-active', selected);
          panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
          panel.inert = !selected;
        });

        this.modeTabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.activateMode(tab));
          tab.addEventListener('keydown', (event) => this.onModeKeydown(event, index));
        });

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.activate(tab));
          tab.addEventListener('keydown', (event) => this.onStyleKeydown(event, index));
        });

        this.stageScroller?.addEventListener('scroll', this.onStageScroll, { passive: true });
        this.caseStageScroller?.addEventListener('scroll', this.onCaseStageScroll, { passive: true });

        this.caseTabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.activateCase(tab));
          tab.addEventListener('keydown', (event) => this.onCaseKeydown(event, index));
        });

        if (window.Shopify && Shopify.designMode) {
          document.addEventListener('shopify:block:select', this.onBlockSelect);
        }
      }

      disconnectedCallback() {
        this.stageScroller?.removeEventListener('scroll', this.onStageScroll);
        this.caseStageScroller?.removeEventListener('scroll', this.onCaseStageScroll);
        if (this.stageScrollFrame) cancelAnimationFrame(this.stageScrollFrame);
        if (this.stageScrollReleaseTimer) clearTimeout(this.stageScrollReleaseTimer);
        if (this.caseStageScrollFrame) cancelAnimationFrame(this.caseStageScrollFrame);
        if (this.caseStageScrollReleaseTimer) clearTimeout(this.caseStageScrollReleaseTimer);
        if (window.Shopify && Shopify.designMode) {
          document.removeEventListener('shopify:block:select', this.onBlockSelect);
        }
      }

      onBlockSelect = (event) => {
        const selectedTab = this.querySelector(`[data-style-tab][id$="${event.detail.blockId}"]`);
        if (selectedTab) {
          const styleMode = this.modeTabs.find((tab) => tab.dataset.configMode === 'style');
          if (styleMode) this.activateMode(styleMode, false);
          this.activate(selectedTab, false);
        }
      };

      onModeKeydown(event, index) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        let nextIndex = index;

        if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.modeTabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.modeTabs.length) % this.modeTabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.modeTabs.length - 1;

        this.activateMode(this.modeTabs[nextIndex]);
        this.modeTabs[nextIndex].focus();
      }

      activateMode(activeTab, focus = false) {
        this.modeTabs.forEach((tab) => {
          const selected = tab === activeTab;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.setAttribute('tabindex', selected ? '0' : '-1');
        });

        this.optionPanels.forEach((panel) => {
          panel.hidden = panel.dataset.configOptions !== activeTab.dataset.configMode;
        });

        if (focus) activeTab.focus();
      }

      onStyleKeydown(event, index) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        let nextIndex = index;

        if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.tabs.length - 1;

        this.activate(this.tabs[nextIndex]);
        this.tabs[nextIndex].focus();
      }

      activate(activeTab, focus = false, syncStage = true) {
        this.tabs.forEach((tab) => {
          const selected = tab === activeTab;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.setAttribute('tabindex', selected ? '0' : '-1');
        });

        this.panels.forEach((panel) => {
          const selected = panel.id === activeTab.getAttribute('aria-controls');
          panel.classList.toggle('is-active', selected);
          panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
          panel.inert = !selected;
        });

        if (syncStage && this.stageScroller) {
          const activeStage = this.stages.find((stage) => stage.dataset.styleKey === activeTab.dataset.styleKey);
          if (activeStage) this.scrollStageTo(activeStage);
        }

        if (this.styleCurrentName) this.styleCurrentName.textContent = activeTab.dataset.styleName || '';
        if (this.styleCurrentDetail) {
          this.styleCurrentDetail.textContent = activeTab.dataset.styleDetail || '';
          this.styleCurrentDetail.hidden = !activeTab.dataset.styleDetail;
        }
        if (this.stylePosition) this.stylePosition.textContent = activeTab.dataset.stylePosition || '';

        if (focus) activeTab.focus();
      }

      scrollStageTo(activeStage) {
        if (!this.stageScroller) return;

        if (this.stageScrollFrame) cancelAnimationFrame(this.stageScrollFrame);
        this.stageScrollLocked = true;
        if (this.stageScrollReleaseTimer) clearTimeout(this.stageScrollReleaseTimer);
        this.stageScrollReleaseTimer = setTimeout(() => { this.stageScrollLocked = false; }, 700);
        this.stageScroller.scrollTo({
          left: activeStage.offsetLeft,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }

      onStageScroll = () => {
        if (!this.stageScroller || !this.tabs.length) return;
        if (this.stageScrollLocked) {
          if (this.stageScrollReleaseTimer) clearTimeout(this.stageScrollReleaseTimer);
          this.stageScrollReleaseTimer = setTimeout(() => { this.stageScrollLocked = false; }, 120);
          return;
        }
        if (this.stageScrollFrame) cancelAnimationFrame(this.stageScrollFrame);

        this.stageScrollFrame = requestAnimationFrame(() => {
          const width = this.stageScroller.clientWidth;
          if (!width) return;
          const nextIndex = Math.max(0, Math.min(this.tabs.length - 1, Math.round(this.stageScroller.scrollLeft / width)));
          const nextTab = this.tabs[nextIndex];
          if (nextTab?.getAttribute('aria-selected') !== 'true') this.activate(nextTab, false, false);
        });
      };

      onCaseKeydown(event, index) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        let nextIndex = index;

        if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.caseTabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.caseTabs.length) % this.caseTabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.caseTabs.length - 1;

        this.activateCase(this.caseTabs[nextIndex]);
        this.caseTabs[nextIndex].focus();
      }

      activateCase(activeTab, syncStage = true) {
        this.caseTabs.forEach((tab) => {
          const selected = tab === activeTab;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.removeAttribute('aria-pressed');
          tab.setAttribute('tabindex', selected ? '0' : '-1');
        });

        this.caseIncluded.forEach((included) => {
          const selected = included.dataset.caseIncluded === activeTab.dataset.caseChoice;
          included.classList.toggle('is-active', selected);
          included.setAttribute('aria-hidden', selected ? 'false' : 'true');
          included.inert = !selected;
        });

        if (syncStage && this.caseStageScroller) {
          const activeStage = this.caseStages.find((stage) => stage.dataset.caseStage === activeTab.dataset.caseChoice);
          if (activeStage) {
            this.caseStageScrollLocked = true;
            if (this.caseStageScrollReleaseTimer) clearTimeout(this.caseStageScrollReleaseTimer);
            this.caseStageScrollReleaseTimer = setTimeout(() => { this.caseStageScrollLocked = false; }, 700);
            this.caseStageScroller.scrollTo({
              left: activeStage.offsetLeft,
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            });
          }
        }

        if (this.caseCurrent) this.caseCurrent.textContent = activeTab.dataset.caseName || '';
        if (this.casePosition) this.casePosition.textContent = activeTab.dataset.casePosition || '';
      }

      onCaseStageScroll = () => {
        if (!this.caseStageScroller || !this.caseTabs.length) return;
        if (this.caseStageScrollLocked) {
          if (this.caseStageScrollReleaseTimer) clearTimeout(this.caseStageScrollReleaseTimer);
          this.caseStageScrollReleaseTimer = setTimeout(() => { this.caseStageScrollLocked = false; }, 120);
          return;
        }
        if (this.caseStageScrollFrame) cancelAnimationFrame(this.caseStageScrollFrame);

        this.caseStageScrollFrame = requestAnimationFrame(() => {
          const width = this.caseStageScroller.clientWidth;
          if (!width) return;
          const nextIndex = Math.max(0, Math.min(this.caseTabs.length - 1, Math.round(this.caseStageScroller.scrollLeft / width)));
          const nextTab = this.caseTabs[nextIndex];
          if (nextTab?.getAttribute('aria-selected') !== 'true') this.activateCase(nextTab, false);
        });
      };
    }
  );
}
